import type { ScrollAction, ActionResult } from '../types/actions';
import { isElementVisible } from '../utils/dom-utils';
import { waitForElement, wait } from '../utils/wait-utils';

export async function executeScrollAction(action: ScrollAction): Promise<ActionResult> {
  const startTime = Date.now();

  try {
    // If selector is provided, scroll to element
    if (action.selector) {
      let element = document.querySelector<HTMLElement>(action.selector);

      // Wait for element if specified
      if (!element && action.waitForElement) {
        element = await waitForElement<HTMLElement>(
          action.selector,
          action.waitForElement
        );
      }

      if (!element) {
        return {
          success: false,
          action,
          error: `Element not found: ${action.selector}`,
          timestamp: Date.now(),
          duration: Date.now() - startTime,
          elementFound: false
        };
      }

      // Check if visible
      if (!isElementVisible(element)) {
        const style = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        const reasons = [];

        if (style.display === 'none') reasons.push('display: none');
        if (style.visibility === 'hidden') reasons.push('visibility: hidden');
        if (style.opacity === '0') reasons.push('opacity: 0');
        if (rect.width === 0 || rect.height === 0) reasons.push(`no dimensions (${rect.width}x${rect.height})`);

        return {
          success: false,
          action,
          error: `Element not visible: ${action.selector}. Reason: ${reasons.join(', ') || 'hidden by parent or CSS'}`,
          timestamp: Date.now(),
          duration: Date.now() - startTime,
          elementFound: true
        };
      }

      // Scroll to element
      element.scrollIntoView({
        behavior: action.behavior || 'smooth',
        block: 'center',
        inline: 'center'
      });

      // Wait for smooth scroll to complete
      if (action.behavior === 'smooth') {
        await wait(500);
      }

      // Get scroll position after scrolling
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      const maxScrollX = Math.max(0, document.documentElement.scrollWidth - window.innerWidth);
      const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

      return {
        success: true,
        action,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true,
        data: {
          scrollPosition: { x: scrollX, y: scrollY },
          maxScroll: { x: maxScrollX, y: maxScrollY },
          inViewport: true
        }
      };
    }

    // If x/y coordinates are provided, scroll window
    if (action.x !== undefined || action.y !== undefined) {
      const x = action.x ?? window.scrollX;
      const y = action.y ?? window.scrollY;

      window.scrollTo({
        top: y,
        left: x,
        behavior: action.behavior || 'smooth'
      });

      // Wait for smooth scroll to complete
      if (action.behavior === 'smooth') {
        await wait(500);
      }

      // Get scroll position after scrolling
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      const maxScrollX = Math.max(0, document.documentElement.scrollWidth - window.innerWidth);
      const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

      return {
        success: true,
        action,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        data: {
          scrollPosition: { x: scrollX, y: scrollY },
          maxScroll: { x: maxScrollX, y: maxScrollY },
          atTop: scrollY <= 0,
          atBottom: scrollY >= maxScrollY - 10,
          atLeft: scrollX <= 0,
          atRight: scrollX >= maxScrollX - 10
        }
      };
    }

    return {
      success: false,
      action,
      error: 'Either selector or x/y coordinates must be provided',
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  }
}
